'use client';

import { useEffect, useState } from 'react';
import { Card, Chip, Eyebrow } from '../../_components/primitives';
import { fetchLeaderboard } from '../lib/api';
import type { LeaderboardResponse } from '../lib/types';

const PAGE_SIZE = 5;

const MOCK_LEADERBOARD: LeaderboardResponse = {
  min_rounds: 5,
  entries: [
    { user_id: 'apex-lime-ghost', total_score: 1492, rounds_played: 184, average_score: 8.11 },
    { user_id: 'dyno-certified', total_score: 1144, rounds_played: 146, average_score: 7.84 },
    { user_id: 'six-speed-seth', total_score: 1594, rounds_played: 212, average_score: 7.52 },
    { user_id: 'tailpipe-tom', total_score: 1316, rounds_played: 178, average_score: 7.39 },
    { user_id: 'fl5-feverdream', total_score: 776, rounds_played: 109, average_score: 7.12 },
  ],
};

export function AllTimeLeaderboard({
  currentUserId,
  useMock = false,
}: {
  currentUserId?: string;
  useMock?: boolean;
}) {
  const [leaderboard, setLeaderboard] = useState<LeaderboardResponse | null>(
    null,
  );
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMock) {
      setLeaderboard(MOCK_LEADERBOARD);
      setLoading(false);
      return;
    }
    fetchLeaderboard()
      .then((l) => setLeaderboard(l))
      .catch(() => setLeaderboard(null))
      .finally(() => setLoading(false));
  }, [useMock]);

  if (loading) return null;
  if (!leaderboard) return null;

  const showThreshold = leaderboard.min_rounds >= 2;
  const hasEntries = leaderboard.entries.length > 0;
  const shown = leaderboard.entries.slice(0, visible);
  const hasMore = visible < leaderboard.entries.length;

  return (
    <Card padded={false}>
      <div style={{ padding: '16px 20px 18px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 12,
            flexWrap: 'wrap',
          }}
        >
          <Eyebrow tone="denim">All-time leaderboard</Eyebrow>
          {showThreshold && <Chip tone="outline">min {leaderboard.min_rounds} rounds</Chip>}
        </div>

        {!hasEntries ? (
          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 12,
              color: 'var(--fg-4)',
              fontWeight: 300,
            }}
          >
            {showThreshold
              ? `Rankings appear once players have completed ${leaderboard.min_rounds}+ rounds.`
              : 'No rankings yet — check back after more players finish rounds.'}
          </p>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {shown.map((e, i) => (
                <LeaderboardRow
                  key={e.user_id}
                  rank={i + 1}
                  name={e.user_id}
                  avg={e.average_score}
                  rounds={e.rounds_played}
                  you={!!currentUserId && e.user_id === currentUserId}
                />
              ))}
            </div>
            {hasMore && (
              <button
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                style={{
                  width: '100%',
                  marginTop: 8,
                  padding: '8px 0',
                  background: 'transparent',
                  border: 0,
                  fontFamily: 'var(--font-sans)',
                  fontSize: 12,
                  color: 'var(--fg-4)',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Show more
              </button>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

function LeaderboardRow({
  rank,
  name,
  avg,
  rounds,
  you,
}: {
  rank: number;
  name: string;
  avg: number;
  rounds: number;
  you: boolean;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '28px minmax(0, 1fr) 56px 60px',
        alignItems: 'center',
        gap: 10,
        padding: '8px 10px',
        borderRadius: 6,
        background: you ? 'var(--denim-soft)' : 'transparent',
        border: you ? '1px solid var(--denim-200)' : '1px solid transparent',
      }}
    >
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          color: you ? 'var(--denim-deep)' : 'var(--fg-4)',
          fontWeight: you ? 600 : 500,
        }}
      >
        #{rank}
      </span>
      <span
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 13,
          color: you ? 'var(--denim-deep)' : 'var(--fg-2)',
          fontWeight: you ? 600 : 400,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {name}
      </span>
      <span
        className="mono"
        style={{
          fontSize: 12,
          color: you ? 'var(--coral-deep)' : 'var(--fg-2)',
          fontWeight: 600,
          textAlign: 'right',
        }}
      >
        {avg.toFixed(1)}
      </span>
      <span
        className="mono"
        style={{
          fontSize: 11,
          color: 'var(--fg-4)',
          textAlign: 'right',
        }}
      >
        · {rounds}r
      </span>
    </div>
  );
}
